import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser';
import { createIdentifier } from '@toeverything/infra';

export interface WorkspaceRequiring2FA {
  workspaceId: string;
  workspaceName: string;
  enforcedAt: string | null;
  gracePeriodActive: boolean;
}

export interface LocalDataLoginResult {
  localDataLoginProof?: string | null;
  localDataAccountId?: string | null;
}

export interface SignInPasswordResult extends LocalDataLoginResult {
  requiresTwoFactor?: boolean;
  preferredMethod?: 'totp' | 'email';
  workspacesRequiring2FA?: WorkspaceRequiring2FA[];
}

/**
 * sl-wq59.6.4: WebAuthn / FIDO2 Passkey — client-side contract.
 *
 * Mirrors the server contract in
 * `packages/backend/server/src/interfaces/webauthn.interface.ts`. Every
 * `challengeRef` issued by an `*Options` call MUST be echoed back on the
 * corresponding `*Verify` call so the server can consume the challenge
 * atomically (Redis GETDEL, sl-wq59.6.2).
 */
export interface WebAuthnRegistrationOptionsResult {
  options: PublicKeyCredentialCreationOptionsJSON;
  challengeRef: string;
}

export interface WebAuthnAuthenticationOptionsResult {
  options: PublicKeyCredentialRequestOptionsJSON;
  challengeRef: string;
}

export interface WebAuthnRegistrationVerifiedResult {
  credentialId: string;
  deviceName: string | null;
}

export interface WebAuthnLoginVerifiedResult extends LocalDataLoginResult {
  user: {
    id: string;
    email: string;
    name: string;
    avatarUrl: string | null;
    emailVerified: boolean;
  };
  userVerified: boolean;
  requiresTwoFactor: boolean;
}

export interface WebAuthnCredentialSummary {
  credentialId: string;
  deviceName: string | null;
  transports: string[];
  createdAt: string;
  lastUsedAt: string | null;
}

export interface AuthProvider {
  signInMagicLink(
    email: string,
    token: string,
    clientNonce?: string
  ): Promise<SignInPasswordResult>;

  signInOauth(
    code: string,
    state: string,
    provider: string,
    clientNonce?: string
  ): Promise<
    LocalDataLoginResult & {
      redirectUri?: string;
      isNewUser?: boolean;
      requiresTwoFactor?: boolean;
      preferredMethod?: 'totp' | 'email';
      email?: string;
    }
  >;

  signInPassword(credential: {
    email: string;
    password: string;
    verifyToken?: string;
    challenge?: string;
  }): Promise<SignInPasswordResult>;

  signOut(): Promise<void>;

  // sl-wq59.6.4: WebAuthn passkey flows
  webauthnRegisterOptions(): Promise<WebAuthnRegistrationOptionsResult>;
  webauthnRegisterVerify(
    challengeRef: string,
    response: RegistrationResponseJSON,
    deviceName?: string
  ): Promise<WebAuthnRegistrationVerifiedResult>;
  webauthnLoginOptions(): Promise<WebAuthnAuthenticationOptionsResult>;
  webauthnLoginVerify(
    challengeRef: string,
    response: AuthenticationResponseJSON
  ): Promise<WebAuthnLoginVerifiedResult>;
  webauthnListCredentials(): Promise<WebAuthnCredentialSummary[]>;
  webauthnRemoveCredential(credentialId: string): Promise<void>;
}

export const AuthProvider = createIdentifier<AuthProvider>('AuthProvider');
